import {NavigateFunction} from "react-router-dom";
import {
  ConfirmationResult,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getIdToken,
  getIdTokenResult,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPhoneNumber,
  reauthenticateWithPopup,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
  User as FirebaseUser,
} from "firebase/auth";
import {AuthKeysDto} from "../dtos/auth-keys.dto";
import {logError} from "./logger.service";
import {firebaseAuth, googleAuthProvider} from "./firebase.service";

export interface User {
  email: string;
}

let phoneRecaptcha: RecaptchaVerifier | null = null;

const sessionIdentity = (): User | null => {
  const user = firebaseAuth.currentUser;
  if (!user) {
    return null;
  }
  return {email: user.email || user.phoneNumber || user.uid};
};

// Thrown by getUserTokenOrRedirect when it has navigated the user to /login.
// Callers should let this propagate (so the doomed request is never made) and
// suppress error toasts for it — we're already redirecting, so there's nothing
// for the user to act on.
export class AuthRedirectError extends Error {
  constructor() {
    super('Redirecting to login');
    this.name = 'AuthRedirectError';
  }
}

export const isAuthRedirectError = (e: unknown): e is AuthRedirectError =>
  e instanceof AuthRedirectError || (e as { name?: string } | null)?.name === 'AuthRedirectError';

// Thrown by provisionUser when the authenticated account is not assigned to any
// company (HTTP 403 from the provisioning endpoint). The sign-in flow catches this
// to sign the user out and prompt them to use a different account.
export class AccountNotAssignedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AccountNotAssignedError';
  }
}

export const isAccountNotAssignedError = (e: unknown): e is AccountNotAssignedError =>
  e instanceof AccountNotAssignedError || (e as { name?: string } | null)?.name === 'AccountNotAssignedError';

// Ensures the signed-in Firebase user has a backing accusite_user. The API creates
// the user (and assigns their company) on first sign-in when their email or phone has been
// pre-assigned; it responds 403 when the account isn't assigned to any company.
export const provisionUser = async (navigate: NavigateFunction): Promise<void> => {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/users/provision`,
    {method: 'POST', headers: {Authorization: `Bearer ${token}`}});
  if (response.ok) {
    return;
  }
  if (response.status === 403) {
    const body = await response.json().catch(() => null);
    throw new AccountNotAssignedError(
      body?.message ?? "Your account isn't assigned to any company.");
  }
  throw new Error(`Failed to provision user: ${response.statusText}`);
};

export const signInWithEmail = async (email: string, password: string) => {
  await signInWithEmailAndPassword(firebaseAuth, email, password);
};

export const signInWithGoogle = async () => {
  await signInWithPopup(firebaseAuth, googleAuthProvider);
};

export const preparePhoneRecaptcha = (containerId: string): RecaptchaVerifier => {
  phoneRecaptcha?.clear();
  phoneRecaptcha = new RecaptchaVerifier(firebaseAuth, containerId, {size: 'invisible'});
  return phoneRecaptcha;
};

export const startPhoneSignIn = async (phoneE164: string): Promise<ConfirmationResult> => {
  if (!phoneRecaptcha) {
    throw new Error('Phone sign-in is not ready.');
  }
  return signInWithPhoneNumber(firebaseAuth, phoneE164, phoneRecaptcha);
};

export const confirmPhoneSignIn = async (confirmation: ConfirmationResult, code: string) => {
  await confirmation.confirm(code);
};

export const clearPhoneRecaptcha = () => {
  phoneRecaptcha?.clear();
  phoneRecaptcha = null;
};

export const signOutUser = async () => {
  await signOut(firebaseAuth);
};

export const getLoggedInUser = () => {
  const identity = sessionIdentity();
  if (!identity) throw new Error("No authenticated user found");
  return identity;
}

export const getLoggedInUserOrEmpty = () => sessionIdentity();

// Mints a Firebase custom token for the current user via the API. Used to hand auth
// off across the web<->native boundary: the recipient calls exchangeCustomToken() to
// establish its own real Firebase session (signInWithCustomToken).
export const getCustomToken = async (navigate: NavigateFunction): Promise<string> => {
  const token = await getUserTokenOrRedirect(navigate);
  const customToken = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/auth/custom-token`,
    {method: 'POST', headers: {Authorization: `Bearer ${token}`}})
    .then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to mint custom token: ${response.statusText}`);
      }
      return (await response.json() as {customToken: string}).customToken;
    });
  return customToken;
}

// Exchanges a Firebase custom token (e.g. one handed back by the native AR app) for
// a real, auto-refreshing Firebase session in this client.
export const exchangeCustomToken = async (customToken: string): Promise<void> => {
  await signInWithCustomToken(firebaseAuth, customToken);
}

async function fetchKeys(navigate: NavigateFunction): Promise<AuthKeysDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const keys = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/auth/keys`,
    {headers: {Authorization: `Bearer ${token}`}})
    .then(async response => {
      if(!response.ok) {
        throw new Error(`Failed to fetch keys: ${response.statusText}`);
      }
      return await response.json() as AuthKeysDto
    });
  return keys;
}

export const requireAuth = async (navigate: NavigateFunction) => {
  await getUserTokenOrRedirect(navigate)
    .catch(e => {
      if (isAuthRedirectError(e)) return;
      logError('Error in require auth', {}, e);
    });
}

// Resolves the current Firebase ID token without redirecting. getIdToken()
// transparently refreshes a near-expiry token. Returns null when there is no
// authenticated Firebase session.
export const getAccessToken = async (): Promise<string | null> => {
  // Firebase restores its persisted session asynchronously; wait for that to
  // settle so a page load doesn't momentarily look unauthenticated.
  await firebaseAuth.authStateReady();
  const user = firebaseAuth.currentUser;
  if (!user) {
    return null;
  }
  return await user.getIdToken();
}

export const getAccessTokenOrThrow = async (): Promise<string> => {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("No authenticated user found");
  }
  return token;
}

export const getUserTokenOrRedirect = async (navigate: NavigateFunction): Promise<string> => {
  const token = await getAccessToken();
  if (!token) {
    navigate('/login');
    throw new AuthRedirectError();
  }
  return token;
}


export const getApiKeys = async (navigate: NavigateFunction): Promise<AuthKeysDto> => {
  let googleMapsApiKey = localStorage.getItem('googleMapsApiKey');
  let datadogApiKey = localStorage.getItem('datadogApiKey');
  if(googleMapsApiKey && datadogApiKey) {
    return {googleMapsApiKey, datadogApiKey}
  }
  return await fetchKeys(navigate)
    .then(keys => {
      googleMapsApiKey = keys.googleMapsApiKey;
      datadogApiKey = keys.datadogApiKey;
      localStorage.setItem('googleMapsApiKey', googleMapsApiKey);
      localStorage.setItem('datadogApiKey', datadogApiKey);
      return {googleMapsApiKey, datadogApiKey}
    })
    .catch(e => {
      logError('Error fetching keys', {}, e);
      navigate('/login');
      throw new Error("Not authenticated");
    })
}

// This is a temporary solution because of how the map JS loader works
export const getApiKeysSync = (navigate: NavigateFunction): AuthKeysDto => {
  let googleMapsApiKey = localStorage.getItem('googleMapsApiKey');
  let datadogApiKey = localStorage.getItem('datadogApiKey');
  if(googleMapsApiKey && datadogApiKey) {
    return {googleMapsApiKey, datadogApiKey}
  }
  navigate('/login');
  throw new Error("Not authenticated");
}


// --- SQ-537: company signup and password reset ------------------------------

// Thrown by signUpCompany when the API refuses with 409: the account already
// runs a company, or a company has already invited this phone / email (joining
// is a sign-in, not a signup). The message is the API's wording.
export class AccountConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AccountConflictError';
  }
}

export const isAccountConflictError = (e: unknown): e is AccountConflictError =>
  e instanceof AccountConflictError || (e as { name?: string } | null)?.name === 'AccountConflictError';

export interface CompanySignupDto {
  companyName: string;
  ownerName: string;
  email?: string | null;
  phoneNumber?: string | null;
  companyPhoneNumber?: string | null;
  /** SQ-419: the checkbox, with the versions the form showed. */
  legal?: {accepted: boolean; termsVersion: string; privacyVersion: string};
}

export interface CompanySignupResultDto {
  companyId: number;
  companyName: string;
  userId: number;
}

// Creates an email + password Firebase account. Email is one of the two ways
// to make an account, never a requirement: phone signup uses startPhoneSignIn
// and confirmPhoneSignIn and then the same signUpCompany call.
export const createAccountWithEmail = async (email: string, password: string) => {
  await createUserWithEmailAndPassword(firebaseAuth, email, password);
};

// Password reset for email accounts. A phone account has no password: a fresh
// text code is its reset.
export const sendPasswordReset = async (email: string) => {
  await sendPasswordResetEmail(firebaseAuth, email);
};

// Turns the signed-in Firebase account (phone or email) into a new company's
// Level 5 owner. POST api/v1/auth/signup.
export const signUpCompany = async (
  navigate: NavigateFunction, dto: CompanySignupDto): Promise<CompanySignupResultDto> => {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/auth/signup`,
    {
      method: 'POST',
      headers: {Authorization: `Bearer ${token}`, 'Content-Type': 'application/json'},
      body: JSON.stringify(dto),
    });
  if (response.ok) {
    return await response.json() as CompanySignupResultDto;
  }
  const body = await response.json().catch(() => null) as {message?: string; details?: string} | null;
  if (response.status === 409) {
    throw new AccountConflictError(body?.message ?? 'This account already belongs to a company.');
  }
  throw new Error(body?.details ?? body?.message ?? `Could not create the company: ${response.statusText}`);
};


// --- Re-authentication before closing the account ----------------------------

const requireCurrentUser = (): FirebaseUser => {
  const user = firebaseAuth.currentUser;
  if (!user) {
    throw new Error("No authenticated user found");
  }
  return user;
};

// The API accepts a closing request only from a sign-in made in the last few
// minutes, so every re-authentication ends by refreshing the ID token: the
// next request then carries the new auth_time.
const refreshAfterReauth = async (user: FirebaseUser) => {
  await getIdToken(user, true);
};

/** How this session signed in: password, phone, google.com, custom, … (null when signed out). */
export const currentSignInMethod = async (): Promise<string | null> => {
  await firebaseAuth.authStateReady();
  const user = firebaseAuth.currentUser;
  if (!user) {
    return null;
  }
  return (await getIdTokenResult(user)).signInProvider;
};

export interface AccountIdentity {
  email: string | null;
  phoneNumber: string | null;
  /** The sign-in methods linked to the account (password, phone, google.com). */
  linkedProviders: string[];
}

export const currentAccountIdentity = (): AccountIdentity => {
  const user = firebaseAuth.currentUser;
  return {
    email: user?.email ?? null,
    phoneNumber: user?.phoneNumber ?? null,
    linkedProviders: user?.providerData.map(info => info.providerId) ?? [],
  };
};

export const reauthenticateWithPassword = async (password: string) => {
  const user = requireCurrentUser();
  if (!user.email) {
    throw new Error("This account has no email");
  }
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
  await refreshAfterReauth(user);
};

export const startPhoneReauth = async (containerId: string): Promise<ConfirmationResult> => {
  const user = requireCurrentUser();
  if (!user.phoneNumber) {
    throw new Error("This account has no phone number");
  }
  return reauthenticateWithPhoneNumber(user, user.phoneNumber, preparePhoneRecaptcha(containerId));
};

export const confirmPhoneReauth = async (confirmation: ConfirmationResult, code: string) => {
  const user = requireCurrentUser();
  await confirmation.confirm(code);
  await refreshAfterReauth(user);
};

export const reauthenticateWithGoogle = async () => {
  const user = requireCurrentUser();
  // A provider of its own: the hint preselects this account in Google's
  // chooser, and the shared sign-in provider stays without one.
  const provider = new GoogleAuthProvider();
  if (user.email) {
    provider.setCustomParameters({login_hint: user.email});
  }
  await reauthenticateWithPopup(user, provider);
  await refreshAfterReauth(user);
};
